import type { Academy } from '~/generated_types';
import type { TrainingObject } from '../../types';
import type { PageObjectOnlyTrainings } from './CustomPageCard.model';
import _ from 'underscore';
import { useMemo } from 'react';
import { APP_CONSTANTS, I18n } from '~/common/explicit-exports';
import AcademyThemeContentCard from '~/components/academies/common/academy-theme-content-card';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import EcommerceUtils from '~/clc/features/academy-ecommerce/utils/ecommerce-utils';

const TRANSLATED_VIEW = I18n.t('js.academy.content_card.view');

const TRANSLATED_COMPLETED = I18n.t('js.academy.content_card.completed');

const TRANSLATED_IN_PROGRESS = I18n.t('js.academy.content_card.in_progress');

const TRANSLATED_PASSED = I18n.t('js.academy.content_card.passed');

const TRANSLATED_FAILED = I18n.t('js.academy.content_card.failed');

const CONTENT_TYPES = APP_CONSTANTS.ASSIGNABLE_EXTERNAL_CONTENT_TYPES;

interface CustomPageGuideCardProps {
  academy: Academy['MINIMUM_THEME_CONTENT'];
  accessibleContentIds: string[];
  className?: string;
  pageSlug: string;
  pageObject: PageObjectOnlyTrainings;
  urlParams?: Record<string, string>;
}

const getUserNeedsToPurchase = (object: TrainingObject, accessibleContentIds: string[]) => {
  if ('amountCents' in object && 'amountProprietaryCurrency' in object) {
    const { amountCents, amountProprietaryCurrency } = object;
    return EcommerceUtils.userNeedsToPurchase(object.id, accessibleContentIds, amountCents, amountProprietaryCurrency);
  }

  return false;
};

export const CustomPageGuideOrResourceCard = ({
  academy,
  accessibleContentIds,
  className,
  pageSlug,
  pageObject,
  urlParams = null,
}: CustomPageGuideCardProps) => {
  const { themeOptions } = academy;
  const { objectType: contentType, learnerObject: object } = pageObject;
  const assignment = 'assignment' in object && object.assignment;

  const contentCardRedirectUrl = useMemo(() => {
    const urlPrefix = pageSlug ? `/pages/${pageSlug}` : '';
    let url;

    if (contentType === CONTENT_TYPES.GUIDE) {
      url = `${urlPrefix}/training/${pageObject.objectId}/overview`;
    }

    if (contentType === CONTENT_TYPES.SCORM && !isFeatureEnabled('scorm_academies')) {
      return undefined;
    }

    if (contentType === CONTENT_TYPES.RESOURCE || contentType === CONTENT_TYPES.SCORM) {
      if (getUserNeedsToPurchase(object, accessibleContentIds)) {
        return '/checkout';
      }

      const slug = window.location.href.split('/').pop();
      const currentPath = window.location.pathname;
      url = `${urlPrefix}/training/${pageObject.objectId}/register`;

      // Only add custom_page_slug if we're on a /pages/{slug} route
      if (currentPath.includes('/pages/')) {
        url += `?custom_page_slug=${slug}`;
      }
    }

    if (url && urlParams) {
      const searchParams = new URLSearchParams(urlParams).toString();
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}${searchParams}`;
    }

    return url;
  }, [pageSlug, contentType, urlParams, pageObject.objectId, object, accessibleContentIds]);

  const progressTextAndColorFromPassStatusMetadata = () => {
    let progressText: string;
    let progressTextColor: string;
    const { internalAssignerId, firstAccessedAt } = assignment ?? {};
    const { didPass, didFail, doesRequireGrading, isInProgress } = assignment?.passStatusMetadata ?? {};
    const { requirePassingScore } = assignment?.course ?? {};

    if (internalAssignerId !== null && !firstAccessedAt) {
      progressText = TRANSLATED_VIEW;
    } else if (isInProgress || _.every([didPass, didFail, doesRequireGrading, isInProgress], (val) => val === false)) {
      // Extra "every" logic here is to handle an edge case where user clicks into a guide, (so now the user would have an assignment)
      // but has not done anything within that guide (i.e. answer a question)
      progressText = TRANSLATED_IN_PROGRESS;
    } else if (requirePassingScore) {
      if (didPass) {
        progressText = TRANSLATED_PASSED;
        progressTextColor = '#4ABF4A';
      } else if (didFail) {
        progressText = TRANSLATED_FAILED;
        progressTextColor = '#ED3E31';
      } else if (doesRequireGrading) {
        progressText = TRANSLATED_COMPLETED;
      } else {
        // Scorm may have an "unknown" status, for which we will show "completed" on learner card
        progressText = TRANSLATED_COMPLETED;
      }
    } else {
      progressText = TRANSLATED_COMPLETED;
    }

    return {
      progressText,
      progressTextColor,
    };
  };

  const { progressText, progressTextColor } = progressTextAndColorFromPassStatusMetadata();

  let doesContentHavePrice = false;
  let userNeedsToPurchase = false;
  let formattedPrice = null;

  if ('amountCents' in object && 'amountProprietaryCurrency' in object && 'currency' in object) {
    const { amountCents, amountProprietaryCurrency, currency } = object;

    doesContentHavePrice = EcommerceUtils.doesContentHavePrice(amountCents, amountProprietaryCurrency);
    if (doesContentHavePrice)
      formattedPrice = amountCents
        ? EcommerceUtils.currencyFormatter(currency, amountCents)
        : amountProprietaryCurrency;

    userNeedsToPurchase = EcommerceUtils.userNeedsToPurchase(
      object.id,
      accessibleContentIds,
      amountCents,
      amountProprietaryCurrency,
    );
  }

  const ecommerceParams = {
    isEcommerceSetUp: academy.isEcommerceSetUp,
    doesUserNeedToPurchaseContent: userNeedsToPurchase,
    doesContentHavePrice,
    formattedPrice,
  };

  const target = 'openLinkDirectlyInNewTab' in object && object.openLinkDirectlyInNewTab ? '_blank' : '';
  const timeEstimateSeconds = 'timeEstimateSeconds' in object ? object.timeEstimateSeconds : null;
  return (
    <AcademyThemeContentCard
      className={className}
      accentColor={themeOptions.accentColor}
      assignmentScore={assignment?.score}
      bannerImage={object.cardBanner}
      ecommerceParams={ecommerceParams}
      cardStyle={themeOptions.cardStyle}
      couldAssignmentScoreChange={assignment?.couldScoreChange}
      description={object.description}
      hasAssignment={Boolean(assignment)}
      name={object.displayTitle}
      progressPercent={assignment?.completionPercentage}
      progressText={progressText}
      progressTextColor={progressTextColor}
      timeEstimateSeconds={timeEstimateSeconds}
      type={contentType}
      url={contentCardRedirectUrl}
      hasCardMetas
      dueAt={assignment?.dueAt}
      target={target}
    />
  );
};
